import {createSelector} from 'reselect';
import Track from '../../utils/tracking';
import userAgent from '../../utils/user-agent';
import V2Components from '../../v2-components';
import getPlaceOrderBillingAddressDetails from '../BillingAddressSelectors/getPlaceOrderBillingAddressDetails';
import {isUsingDifferentBillingAddress} from '../DeliveryAddressSelectors/DeliveryAddressSelectors';
import {getNewSelectedDeliveryOptionRequestData} from '../DeliveryOptionsSelectors/DeliveryOptionsSelectors';
import {isMobile} from '../DeviceSelectors';
import getAdyenV2PlaceOrderBillingAdress from '../PaymentOptionSelectors/AdyenV2/getAdyenV2PlaceOrderBillingAddress';
import getAdyenV2PlaceOrderData from '../PaymentOptionSelectors/AdyenV2/getAdyenV2PlaceOrderData';
import makeGetDoesAdyenV2SubTypeRequireGenericBillingAddress
from '../PaymentOptionSelectors/AdyenV2/makeGetDoesAdyenV2SubTypeRequireGenericBillingAddress';
import getShouldStorePaymentDetailsForSelectedPaymentOption
from '../PaymentOptionSelectors/getShouldStorePaymentDetailsForSelectedPaymentOption';
import makeGetDoesOpttySubTypeRequireGenericBillingAddress
from '../PaymentOptionSelectors/Optty/makeGetDoesOpttySubTypeRequireGenericBillingAddress';
import {
  getContactNumber,
  getSelectedAddressInAPIFormat,
  getSelectedDeliveryCountry,
  getSelectedPaymentOption,
  hasDigitalConsent,
  isConsentGiven,
} from '../selectors';
import getAppliedGiftcardsForPlaceOrder from './getAppliedGiftcardsForPlaceOrder';
import getPayerName from './getPayerName';
import getReCaptchaToken from './getRecaptchaToken';
import {
  getArvatoData,
  getGooglePayData,
  getOrderEmailAddress,
  getOrderNewCardDetails,
  getOrderStoredCardDetails,
  getPaymentName,
  getPlaceOrderDeliveryAddress,
  getResidentID,
  isOrderOptedIntoMarketing,
} from './PlaceOrderPropertiesSelectors';

export const getOrderPayment = (state) => {
  const method = getPaymentName(state);
  const paymentOption = getSelectedPaymentOption(state);
  
  if (paymentOption.isAdyenV2Option) {
    // This is a bit messy due to getPlaceOrderBillingAddressDetails returning an object regardless of whether or not the
    // selected payment option actually displayed a billing address. This logic ensures we pass null to API if billing address
    // was not required (and therefore not shown)
    const genericBillingAddress = makeGetDoesAdyenV2SubTypeRequireGenericBillingAddress(paymentOption.type)(state) ? getPlaceOrderBillingAddressDetails(state) : null;
    
    return {
      method: 'adyen_v2',
      additionalPaymentMethodDetailsMap: getAdyenV2PlaceOrderData(state),
      billingAddress: getAdyenV2PlaceOrderBillingAdress(state) || genericBillingAddress,
      channel: 'WEB',
      originUrl: window.location.origin,
    };
  }
  
  if (paymentOption.isOpttyOption) {
    const genericBillingAddress = makeGetDoesOpttySubTypeRequireGenericBillingAddress(paymentOption.type)(state) ? getPlaceOrderBillingAddressDetails(state) : null;
    
    return {
      method: 'optty',
      providerName: paymentOption.type,
      billingAddress: genericBillingAddress,
    };
  }
  
  if (!method) {
    Track.error('Attempted to submit order with no order.payment.method');
    return {};
  }
  
  switch (method) {
    case 'new-credit-card':
      return getOrderNewCardDetails(state);
    case 'stored-credit-card':
      return getOrderStoredCardDetails(state);
    case 'adyen-klarna':
      return {
        ...V2Components.get('adyen-klarna').getData(),
        countryCode: getSelectedDeliveryCountry(state),
        method: 'adyen-klarna',
      };
    case 'klarna':
      return {
        method: 'klarna',
      };
    case 'wechatpay':
      return {
        userAgentString: userAgent.getUserAgent(),
        method: 'wechatpay',
        mobile: isMobile(state),
      };
    case 'klarna-later':
    case 'klarna-now':
    case 'klarna-slice': // intentional fall-through
      return {
        method: method.replace('-', '_'),
      };
    case 'pay-on-delivery':
      return {
        method: method.replaceAll('-', '_'),
      };
    case 'alipay-plus':
      return {
        method: method.replace('-', '_'),
        billingAddress: getPlaceOrderBillingAddressDetails(state),
      };
    case 'frasers-plus':
      return {
        method: method.replace('-', '_'),
      };
    case 'googlepay':
      return getGooglePayData(state);
    case 'paypal-in-3':
    case 'paypal':
      return {
        method: 'paypal',
        express: state?.expressCheckout?.usingExpressCheckout,
      };
    case 'zippay':
    case 'quadpay':
      return {
        method,
        firstName: state.zippay.firstName,
        lastName: state.zippay.lastName,
      };
    case 'spotii':
      return {
        method,
        firstName: state.spotii.firstName,
        lastName: state.spotii.lastName,
      };
    case 'arvato': {
      return {
        method,
        ...getArvatoData(state),
      };
    }
    case 'openpay':
      return {
        method,
        firstName: state.openpay.firstName,
        lastName: state.openpay.lastName,
      };
    default:
      return {method};
  }
};

export const getPlaceOrderBody = createSelector(
  [getSelectedAddressInAPIFormat, getContactNumber, getPlaceOrderDeliveryAddress, getOrderEmailAddress, isOrderOptedIntoMarketing, getOrderPayment, getResidentID, hasDigitalConsent, getNewSelectedDeliveryOptionRequestData, isConsentGiven, getPayerName, getReCaptchaToken, getShouldStorePaymentDetailsForSelectedPaymentOption, getAppliedGiftcardsForPlaceOrder],
  (selectedAddress, contactNumber, deliveryAddress, emailAddress, optedIntoMarketing, payment, residentId, hasDigitalConsent, deliverySelection, consentGiven, payerName, reCaptchaToken, consentToReuse, giftCards) => ({
    fullName: selectedAddress?.addressee,
    payerName,
    contactNumber,
    deliveryAddress,
    emailAddress,
    optedIntoMarketing,
    payment: {
      ...payment,
      consentToReuse,
    },
    residentId,
    hasDigitalConsent,
    deliverySelection,
    consentGiven,
    reCaptchaToken,
    giftCards,
  }),
);

export const getPaymentOnlyOrderBody = createSelector(
  [getOrderPayment, isUsingDifferentBillingAddress],
  (payment, usingDifferentBillingAddress) => {
    if (payment.method !== 'new-credit-card' || usingDifferentBillingAddress) {
      return {payment};
    }
    
    return {
      payment: {
        ...payment,
        billingAddress: {
          addressId: '1001',
        },
      },
    };
  },
);
